import * as React from 'react';
import { useEffect, useState } from 'react';
import { Button, ColumnLayout, Container, Flashbar, Header, SpaceBetween, Tabs } from '@amzn/awsui-components-react-v3';
import {
  convertToDgsHCResourceArn,
  convertToDgsHCResourceId,
  CopiableText,
  fetchTemplatesForResourceId,
  isDGSAdmin,
} from 'src/commons/common';
import { WSDatabaseList } from 'src/components/workspaces/dataBrowse/catalog/databaseList';
import { listCatalogs } from 'src/api/catalog';
import { TABLE_CONTENT_TYPE } from 'src/commons/constants';
import { isValidWorkspace } from 'src/commons/validationUtils';
import { createGroupDetailLink, createWorkspaceDetailLink } from 'src/routes';
import MetadataDetails from 'src/components/workspaces/common/metadataDetails';
import BusinessGlossaries from 'src/components/workspaces/common/businessGlossaries';
import { AdvisoriesForResourceTable } from 'src/components/dataadvisory/listAdvisoriesPage/advisoriesForResource';
import { TemplatesForResourceDetailsTable } from 'src/components/templates/TemplatesForResourceDetailsTable';
import { CatalogEditModal } from 'src/components/workspaces/dataBrowse/catalog/catalogEdit';
import { enableAdvisories } from 'src/api/config';
import ContactInfo from '../../common/ContactInfo';
import { lakeFormationIcon, redshiftIcon } from 'src/components/workspaces/common/common';
import { ResourceNotFound } from 'src/commons/ResourceNotFound';

export interface CatalogDetailProps {
  setContentType: any;
  activeGroup: string;
  username: string;
  activeWorkspace: any;
  databaseName: any;
  match: any;
  setActiveCatalogName: any;
  catalogMap: any;
  userInfo: any;
}

export const CatalogDetail = (props: CatalogDetailProps) => {
  const [catalog, setCatalog] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [templates, setTemplates] = useState(undefined);
  const [loadingTemplates, setLoadingTemplates] = useState<boolean>(true);
  const [editCatalogModalVisible, setEditCatalogModalVisible] = useState(false);

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    handleRefresh();
  }, []);

  const handleRefresh = async () => {
    setLoading(true);
    const catalogs = await listCatalogs({
      Filter: {
        CatalogKeyList: [
          {
            CatalogId: props.match.params.catalogid,
            Region: props.match.params.region,
            ClusterIdentifier: props.match.params.clustername,
            RedshiftWorkgroupName: props.match.params.workgroupname,
            DataSourceId: props.match.params.datasourceid,
          },
        ],
      },
    });

    let catalog = catalogs.CatalogInfoList[0];
    props.setActiveCatalogName(catalog?.Name);
    await fetchTemplatesForCatalog(catalog);
    setCatalog(catalog);
    setLoading(false);
  };

  const getTemplateForCatalog = () => {
    return (
      <TemplatesForResourceDetailsTable
        items={templates}
        loading={loadingTemplates}
        userOwnsResource={userOwnsCatalog()}
      />
    );
  };

  const fetchTemplatesForCatalog = async (catalog) => {
    setLoadingTemplates(true);
    let resourceId = convertToDgsHCResourceId(
      catalog?.CatalogId,
      catalog?.ClusterIdentifier,
      catalog?.RedshiftWorkgroupName,
      undefined,
      undefined,
      undefined,
      undefined,
      catalog?.DataSourceId,
    );
    let fetchedTemplates = await fetchTemplatesForResourceId(resourceId);
    setTemplates(fetchedTemplates);
    setLoadingTemplates(false);
  };

  const closeEditModal = () => {
    setEditCatalogModalVisible(false);
  };

  const getCatalogDetailsMetadata = () => {
    const catalog_metadata = [];

    if (catalog?.Name) {
      catalog_metadata.push(<CopiableText name={'Catalog name'} key={'Catalog name'} value={catalog?.Name} />);
    }

    if (catalog?.CatalogId) {
      catalog_metadata.push(<CopiableText name={'Catalog ID'} key={'Catalog ID'} value={catalog?.CatalogId} />);
    }

    if (catalog?.Owner) {
      catalog_metadata.push(
        <CopiableText
          name={'Owner'}
          key={'Owner'}
          value={catalog?.Owner}
          url={
            isValidWorkspace(catalog?.Owner)
              ? createWorkspaceDetailLink(catalog?.Owner)
              : createGroupDetailLink(catalog?.Owner)
          }
        />,
      );
    }

    if (catalog?.CTI) {
      catalog_metadata.push(<CopiableText name={'CTI'} key={'CTI'} value={catalog?.CTI} />);
    }
    if (catalog?.Region) {
      catalog_metadata.push(<CopiableText name={'Region'} key={'Region'} value={catalog?.Region} />);
    }
    if (catalog?.Description) {
      catalog_metadata.push(<CopiableText name={'Description'} key={'Description'} value={catalog?.Description} />);
    }
    if (catalog?.ClusterIdentifier) {
      catalog_metadata.push(
        <CopiableText name={'Cluster name'} key={'ClusterName'} value={catalog?.ClusterIdentifier} />,
      );
    }
    if (catalog?.RedshiftWorkgroupName) {
      catalog_metadata.push(
        <CopiableText name={'Workgroup name'} key={'WorkgroupName'} value={catalog?.RedshiftWorkgroupName} />,
      );
    }
    if (catalog?.CreatedBy) {
      catalog_metadata.push(<CopiableText name={'Created by'} key={'CreatedBy'} value={catalog?.CreatedBy} />);
    }
    if (catalog?.CreatedOn) {
      catalog_metadata.push(<CopiableText name={'Created on'} key={'CreatedOn'} value={catalog?.CreatedOn} />);
    }
    return catalog_metadata;
  };

  const setNotification = async (header, message) => {
    if (header === 'success') {
      setNotifications([
        {
          type: 'success',
          content: message,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    } else {
      setNotifications([
        {
          header: header,
          type: 'error',
          content: message,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    }
  };

  const userOwnsCatalog = () => {
    if (!catalog || !props.userInfo) return false;
    if (isDGSAdmin(props.userInfo.memberGroupIds)) return true;
    // check if catalog owner is in user workspaces
    // authZ will happen on backend APIs, based on roles
    // Safely handle potentially undefined arrays with nullish coalescing
    const allOwnerIds = [...(props.userInfo.memberWorkspaceIds ?? []), ...(props.userInfo.memberGroupIds ?? [])];
    return allOwnerIds.includes(catalog.Owner);
  };

  const getArn = () => {
    return convertToDgsHCResourceArn(
      catalog?.CatalogId,
      catalog?.ClusterIdentifier,
      catalog?.RedshiftWorkgroupName,
      undefined,
      undefined,
      undefined,
      undefined,
      catalog?.DataSourceId,
    );
  };

  let tabs = [
    {
      label: 'Details',
      id: 'Details',
      content: (
        <SpaceBetween size={'m'} direction={'vertical'}>
          {catalog !== undefined && (
            <CatalogEditModal
              {...props}
              catalog={catalog}
              visible={editCatalogModalVisible}
              dismiss={closeEditModal}
              notify={setNotification}
              handleRefresh={handleRefresh}
            />
          )}
          <Container
            header={
              <Header
                variant={'h2'}
                actions={
                  userOwnsCatalog() && (
                    <Button
                      variant='normal'
                      onClick={() => {
                        setEditCatalogModalVisible(true);
                      }}
                    >
                      Edit
                    </Button>
                  )
                }
              >
                Catalog details
              </Header>
            }
          >
            <ColumnLayout columns={3} borders='horizontal'>
              {getCatalogDetailsMetadata()}
            </ColumnLayout>
          </Container>
          <SpaceBetween size={'l'}>
            {!loading && catalog && <ContactInfo resource={getArn()} />}
            {!loading && catalog && (
              <BusinessGlossaries
                activeGroup={props.activeGroup}
                activeWorkspace={props.activeWorkspace}
                match={''}
                setContentType={props.setContentType}
                resource={getArn()}
                isOwner={userOwnsCatalog()}
              />
            )}
          </SpaceBetween>
        </SpaceBetween>
      ),
    },
    {
      label: 'Databases',
      id: 'Databases',
      contentRenderStrategy: 'eager',
      content: (
        <WSDatabaseList
          {...props}
          loading={loading}
          catalogId={props.match.params.catalogid}
          catalogName={catalog?.Name}
          catalogRegion={catalog?.Region}
          clusterIdentifier={catalog?.ClusterIdentifier}
          redshiftWorkgroupName={catalog?.RedshiftWorkgroupName}
          dataSourceId={catalog?.DataSourceId}
        />
      ),
    },
    {
      label: 'Templates',
      id: 'Templates',
      content: getTemplateForCatalog(),
    },
    {
      label: 'Metadata',
      id: 'Metadata',
      content: (
        <MetadataDetails
          resourceOwnerIds={catalog?.Owner}
          resource={getArn()}
          activeGroup={props.activeGroup}
          activeWorkspace={props.activeWorkspace}
          setContentType={props.setContentType}
          setNotification={setNotification}
        />
      ),
    },
  ];
  if (enableAdvisories()) {
    tabs.push({
      label: 'Advisories',
      id: 'advisories',
      content: (
        <AdvisoriesForResourceTable
          resourceArn={getArn()}
          activeGroup={props.activeGroup}
          setContentType={props.setContentType}
        />
      ),
    });
  }

  if (!catalog && !loading) {
    return (
      <ResourceNotFound
        title={'Catalog not found'}
        subtitle={
          `The given catalog is not valid, or you do not have permission to view it. Please\n` +
          ' check the URL for mistakes and try again.'
        }
      />
    );
  }

  return (
    <SpaceBetween size={'m'} direction={'vertical'}>
      {notifications?.length > 0 && <Flashbar items={notifications} />}
      <Header description={catalog?.Description} variant={'h1'}>
        {props.match.params.clustername || props.match.params.workgroupname ? redshiftIcon : lakeFormationIcon}{' '}
        {catalog?.Name}
      </Header>
      <Tabs tabs={tabs} />
    </SpaceBetween>
  );
};
